import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { WifiOff, RefreshCw, CheckCircle2, CloudOff } from 'lucide-react'
import { useReadonlyFallback, useReadonlyReason, useElectricAvailable } from '@hooks/useReadonlyFallback'

/** navigator.onLine 订阅（设备在线状态） */
function useNavigatorOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb)
      window.addEventListener('offline', cb)
      return () => {
        window.removeEventListener('online', cb)
        window.removeEventListener('offline', cb)
      }
    },
    () => typeof navigator !== 'undefined' && navigator.onLine,
    () => true
  )
}

/** 动态省略号（… 1-3 点循环，1s/拍）：恢复中/重试中的"正在进行"视觉信号 */
function useAnimatedEllipsis(): string {
  const [dots, setDots] = useState(1)
  useEffect(() => {
    const timer = window.setInterval(() => {
      setDots((d) => (d % 3) + 1)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [])
  return '.'.repeat(dots)
}

// 横幅动效（组件内嵌，避免污染全局样式命名空间）：
// - banner-rise：状态横幅从底部滑入（Material snackbar 式入场，行业主流）
// - banner-pop：恢复成功提示弹入（轻微过冲，吸引用户注意状态已变化）
const BANNER_KEYFRAMES = `
@keyframes banner-rise {
  from { transform: translateY(110%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes banner-pop {
  0%   { transform: scale(0.85); opacity: 0; }
  60%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); }
}
`

/**
 * ★ 网络状态统一横幅（全局常驻，App 层挂载，唯一提示面）
 *
 * 四态（优先级从上到下，取首个命中）：
 *   1. 退出闪现（绿色，3s 自动消失 + 弹入动画 + 勾选图标）：只读刚退出 = 云端已对齐，恢复可编辑
 *   2. 离线只读（琥珀，长驻 + 图标呼吸脉冲）：设备离线，显示上次同步的数据
 *   3. 网络不稳定（琥珀，长驻 + 图标慢旋转 + 动态省略号）：设备在线但数据服务不可达，只读重试中
 *   4. 恢复中（蓝色，长驻 + 图标脉冲 + 动态省略号）：网络/服务已恢复，云端增量对齐完成前保持只读
 *
 * 显眼性设计（2026-09-19，行业主流方法）：
 *   - 状态横幅滑入入场（snackbar rise）、图标 + 文字加粗（font-medium）+ 阴影加深
 *   - 持续态图标动画（呼吸/旋转）让用户余光可感知"状态仍在"
 *   - 恢复成功用弹入 + 勾选图标强调"变了"这一事件
 *
 * 恢复态动态信号：横幅期间 startReadonlyRecoveryProbe 持续探测（2s 周期），
 *   省略号动画传达"正在工作"；对齐成功 → 只读退出 → 绿色 3s 闪现后彻底消失。
 */
const ReadonlyModeBanner: React.FC = () => {
  const active = useReadonlyFallback()
  const reason = useReadonlyReason()
  const online = useNavigatorOnline()
  const electricUp = useElectricAvailable()
  const ellipsis = useAnimatedEllipsis()

  // 退出闪现：只读 active 从 true → false 时显示 3s 成功提示
  const [exitFlash, setExitFlash] = useState(false)
  const prevActiveRef = useRef(false)
  useEffect(() => {
    const prev = prevActiveRef.current
    prevActiveRef.current = active
    if (prev && !active) {
      setExitFlash(true)
      const t = window.setTimeout(() => setExitFlash(false), 3000)
      return () => window.clearTimeout(t)
    }
  }, [active])

  if (!active && !exitFlash) return null

  // 文案与配色四态判定（reason 仅诊断用，文案由实时状态判定）
  let text: string
  let tone: 'amber' | 'blue' | 'green'
  let icon: React.ReactNode
  if (!active && exitFlash) {
    text = '已恢复在线同步，可正常编辑'
    tone = 'green'
    icon = <CheckCircle2 className="w-4 h-4 shrink-0" />
  } else if (!online) {
    text = '离线只读 · 显示上次同步的数据，联网后自动恢复'
    tone = 'amber'
    icon = <WifiOff className="w-4 h-4 shrink-0 animate-pulse" />
  } else if (!electricUp) {
    text = `网络不稳定 · 只读模式，正在重试连接${ellipsis}`
    tone = 'amber'
    icon = <RefreshCw className="w-4 h-4 shrink-0 animate-[spin_2.5s_linear_infinite]" />
  } else {
    text = `网络已恢复，正在同步最新内容${ellipsis}`
    tone = 'blue'
    icon = <CloudOff className="w-4 h-4 shrink-0 animate-pulse" />
  }

  const toneClass =
    tone === 'green'
      ? 'text-green-700 dark:text-green-300 bg-green-100/95 dark:bg-green-900/90 border-green-300 dark:border-green-700'
      : tone === 'blue'
        ? 'text-blue-600 dark:text-blue-300 bg-blue-100/95 dark:bg-blue-900/90 border-blue-300 dark:border-blue-700'
        : 'text-amber-700 dark:text-amber-300 bg-amber-100/95 dark:bg-amber-900/90 border-amber-300 dark:border-amber-700'

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 flex justify-center px-4 pointer-events-none"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 4px)' }}
    >
      <style>{BANNER_KEYFRAMES}</style>
      <div
        className={`flex items-center gap-1.5 text-xs font-medium px-3.5 py-2 rounded-t-lg shadow-md border ${toneClass} ${
          tone === 'green' ? 'banner-pop' : 'banner-rise'
        }`}
        style={{
          animation: tone === 'green' ? 'banner-pop 0.32s ease-out' : 'banner-rise 0.3s ease-out',
        }}
      >
        {icon}
        <span>{text}</span>
      </div>
    </div>
  )
}

export default ReadonlyModeBanner
